import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { getProductsAdmin, createProduct, updateProduct, deleteProduct } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { PRODUCT_CATEGORIES, categoryLabel } from "@/constants/categories";
import { ShoppingBag, Plus, PencilSimple, Trash, X } from "@phosphor-icons/react";

const EMPTY = {
  name_gr: "", name_en: "", description_gr: "", description_en: "",
  price: "", category: "other", image_url: "", active: true,
};

export const AdminProducts = () => {
  const { t, lang } = useLang();
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    getProductsAdmin().then(setProducts).catch(() => setProducts([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setForm(EMPTY); setEditingId(null); setShowForm(true); };
  const openEdit = (p) => {
    setForm({
      name_gr: p.name_gr, name_en: p.name_en,
      description_gr: p.description_gr || "", description_en: p.description_en || "",
      price: String(p.price), category: p.category || "other",
      image_url: p.image_url || "", active: p.active,
    });
    setEditingId(p.id);
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name_gr || !form.name_en || form.price === "") {
      toast.error(t.errors.required);
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      price: parseFloat(form.price),
      description_gr: form.description_gr || null,
      description_en: form.description_en || null,
      category: form.category || null,
      image_url: form.image_url || null,
    };
    try {
      if (editingId) await updateProduct(editingId, payload);
      else await createProduct(payload);
      toast.success(t.adminProducts.saved);
      setShowForm(false);
      load();
    } catch { toast.error(t.errors.generic); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await deleteProduct(id); toast.success(t.adminProducts.deleted); load(); }
    catch { toast.error(t.errors.generic); }
  };

  const name = (p) => (lang === "gr" ? p.name_gr : p.name_en);

  return (
    <div className="mt-8 rounded-2xl border border-[#20261E]/10 bg-white p-8 shadow-[0_8px_32px_rgba(28,37,29,0.04)]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShoppingBag size={22} className="text-[#24483A]" />
          <h2 className="font-serif text-2xl tracking-tight">{t.adminProducts.title}</h2>
        </div>
        <Button data-testid="add-product-btn" onClick={openAdd}
          className="rounded-full bg-[#24483A] px-5 text-white hover:bg-[#163024]">
          <Plus size={16} className="mr-1" /> {t.adminProducts.add}
        </Button>
      </div>

      {showForm && (
        <div data-testid="product-form" className="mt-6 rounded-xl border border-[#20261E]/10 bg-[#F1EEE4] p-6">
          <div className="mb-4 flex items-center justify-between">
            <span className="font-semibold text-[#20261E]">
              {editingId ? t.adminProducts.edit : t.adminProducts.add}
            </span>
            <button data-testid="close-product-form" onClick={() => setShowForm(false)} className="text-[#8F8A79]">
              <X size={18} />
            </button>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="mb-2 block text-sm text-[#5B6053]">{t.adminProducts.nameGr}</Label>
              <Input data-testid="product-name-gr" value={form.name_gr}
                onChange={(e) => setForm({ ...form, name_gr: e.target.value })}
                className="rounded-xl border-[#20261E]/10 bg-white" />
            </div>
            <div>
              <Label className="mb-2 block text-sm text-[#5B6053]">{t.adminProducts.nameEn}</Label>
              <Input data-testid="product-name-en" value={form.name_en}
                onChange={(e) => setForm({ ...form, name_en: e.target.value })}
                className="rounded-xl border-[#20261E]/10 bg-white" />
            </div>
            <div>
              <Label className="mb-2 block text-sm text-[#5B6053]">{t.adminProducts.descGr}</Label>
              <Textarea data-testid="product-desc-gr" value={form.description_gr}
                onChange={(e) => setForm({ ...form, description_gr: e.target.value })}
                className="rounded-xl border-[#20261E]/10 bg-white" />
            </div>
            <div>
              <Label className="mb-2 block text-sm text-[#5B6053]">{t.adminProducts.descEn}</Label>
              <Textarea data-testid="product-desc-en" value={form.description_en}
                onChange={(e) => setForm({ ...form, description_en: e.target.value })}
                className="rounded-xl border-[#20261E]/10 bg-white" />
            </div>
            <div>
              <Label className="mb-2 block text-sm text-[#5B6053]">{t.adminProducts.price}</Label>
              <Input data-testid="product-price" type="number" step="0.01" value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                className="rounded-xl border-[#20261E]/10 bg-white" />
            </div>
            <div>
              <Label className="mb-2 block text-sm text-[#5B6053]">{t.adminProducts.category}</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="product-category" className="rounded-xl border-[#20261E]/10 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRODUCT_CATEGORIES.map((c) => (
                    <SelectItem key={c.key} value={c.key} data-testid={`category-option-${c.key}`}>
                      {lang === "gr" ? c.gr : c.en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="mb-2 block text-sm text-[#5B6053]">{t.adminProducts.imageUrl}</Label>
              <Input data-testid="product-image-url" value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                placeholder="https://..."
                className="rounded-xl border-[#20261E]/10 bg-white" />
            </div>
          </div>
          <label className="mt-4 flex cursor-pointer items-center gap-3">
            <Checkbox data-testid="product-active" checked={form.active}
              onCheckedChange={(v) => setForm({ ...form, active: !!v })} />
            <span className="text-sm text-[#20261E]">{t.adminProducts.active}</span>
          </label>
          <div className="mt-5 flex gap-3">
            <Button data-testid="save-product" disabled={saving} onClick={save}
              className="rounded-full bg-[#24483A] px-6 text-white hover:bg-[#163024]">
              {t.adminProducts.save}
            </Button>
            <Button variant="ghost" onClick={() => setShowForm(false)}
              className="rounded-full px-6 text-[#5B6053]">
              {t.adminProducts.cancel}
            </Button>
          </div>
        </div>
      )}

      <div data-testid="admin-products-list" className="mt-6 space-y-3">
        {products.length === 0 && <p className="text-sm text-[#8F8A79]">{t.adminProducts.none}</p>}
        {products.map((p) => (
          <div key={p.id} data-testid={`admin-product-${p.id}`}
            className="flex items-center justify-between gap-4 rounded-xl border border-[#20261E]/10 p-4">
            <div className="flex items-center gap-4">
              <img src={p.image_url || "https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?w=120&q=80"}
                alt={name(p)} className="h-14 w-14 rounded-lg object-cover" />
              <div>
                <div className="font-semibold text-[#20261E]">
                  {name(p)}
                  {!p.active && <span className="ml-2 rounded-full bg-[#F0F0F0] px-2 py-0.5 text-xs text-[#A3A3A3]">{t.adminProducts.inactive}</span>}
                </div>
                <div className="text-sm text-[#5B6053]">
                  €{Number(p.price).toFixed(2).replace(/\.00$/, "")} · {categoryLabel(p.category, lang)}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button data-testid={`edit-product-${p.id}`} variant="outline" onClick={() => openEdit(p)}
                className="h-9 rounded-full border-[#20261E]/10 px-3 text-xs text-[#5B6053]">
                <PencilSimple size={14} className="mr-1" /> {t.adminProducts.edit}
              </Button>
              <Button data-testid={`delete-product-${p.id}`} variant="outline" onClick={() => remove(p.id)}
                className="h-9 rounded-full border-red-200 px-3 text-xs text-red-600 hover:bg-red-50">
                <Trash size={14} className="mr-1" /> {t.adminProducts.delete}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
